import { useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useSession } from "@/auth/SessionContext";
import { api } from "@/lib/api";
import { focusFirstError } from "@/lib/formA11y";
import { ApiClientError } from "@/lib/types";
import { Button } from "@/components/Button";
import { Field, PasswordInput, TextInput } from "@/components/Field";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import { StatePanel } from "@/components/StatePanel";
import { Wordmark } from "@/components/Wordmark";
import { useLocale } from "@/locale/LocaleContext";
import ui from "@/components/ui.module.css";
import styles from "./auth.module.css";

const adminHost = window.location.hostname === "agent-admin.cloud.tenqora.net";

export function LoginPage() {
  const { session, applySession, loading } = useSession();
  const { m } = useLocale();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && session) return <Navigate to={adminHost ? "/admin" : "/app"} replace />;

  if (adminHost) return (
    <AuthLayout title={m.admin.title} lead={m.auth.loginTitle}>
      <a className={ui.panel} href="/api/v1/auth/cloud">{m.common.signIn} · Cloud</a>
    </AuthLayout>
  );

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!email.trim()) next.email = m.auth.emailRequired;
    if (!password) next.password = m.auth.passwordRequired;
    setErrors(next);
    if (Object.keys(next).length) {
      queueMicrotask(() => focusFirstError(next, ["email", "password"]));
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      const payload = await api.login({ email: email.trim(), password });
      applySession(payload);
      navigate(adminHost ? "/admin" : "/app");
    } catch (err) {
      setFormError(err instanceof ApiClientError ? err.message : m.auth.loginFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title={adminHost ? m.admin.title : m.auth.loginTitle} lead={adminHost ? m.auth.loginTitle : m.auth.loginLead}>
      <form noValidate onSubmit={onSubmit} className={ui.panel} style={{ display: "grid", gap: "0.9rem" }}>
        <Field label={m.auth.email} htmlFor="email" error={errors.email}>
          <TextInput
            name="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label={m.auth.password} htmlFor="password" error={errors.password}>
          <PasswordInput
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {formError ? <StatePanel kind="error" title={m.auth.loginFailed} message={formError} /> : null}
        <Button type="submit" disabled={busy} aria-busy={busy || undefined}>
          {busy ? m.common.signingIn : m.auth.loginAction}
        </Button>
        {!adminHost && <p className={ui.meta}>
          {m.auth.noAccount} <Link to="/register">{m.common.register}</Link>
        </p>}
      </form>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const { session, applySession, loading } = useSession();
  const { m } = useLocale();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [organization, setOrganization] = useState("");
  const [password, setPassword] = useState("");
  const [ack, setAck] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && session) return <Navigate to="/app" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = m.auth.nameRequired;
    if (!email.trim()) next.email = m.auth.emailRequired;
    if (!organization.trim()) next.organization = m.auth.orgRequired;
    if (password.length < 12) next.password = m.auth.passwordMin;
    if (!ack) next.ack = m.auth.ackRequired;
    setErrors(next);
    if (Object.keys(next).length) {
      queueMicrotask(() => focusFirstError(next, ["name", "email", "organization", "password", "ack"]));
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      const payload = await api.register({
        name: name.trim(),
        email: email.trim(),
        password,
        organization: organization.trim(),
      });
      applySession(payload);
      navigate("/app");
    } catch (err) {
      setFormError(err instanceof ApiClientError ? err.message : m.auth.registerFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title={m.auth.registerTitle} lead={m.auth.registerLead}>
      <form noValidate onSubmit={onSubmit} className={ui.panel} style={{ display: "grid", gap: "0.9rem" }}>
        <Field label={m.auth.name} htmlFor="name" error={errors.name}>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </Field>
        <Field label={m.auth.email} htmlFor="email" error={errors.email}>
          <TextInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </Field>
        <Field label={m.auth.organization} htmlFor="organization" error={errors.organization}>
          <TextInput
            value={organization}
            onChange={(e) => setOrganization(e.target.value)}
            autoComplete="organization"
          />
        </Field>
        <Field label={m.auth.password} htmlFor="password" error={errors.password} hint={m.auth.passwordMin}>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </Field>
        <div className={ui.field}>
          <label htmlFor="ack" style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start" }}>
            <input
              id="ack"
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              aria-invalid={errors.ack ? true : undefined}
              aria-describedby={errors.ack ? "ack-error" : undefined}
              style={{ marginTop: "0.25rem" }}
            />
            <span>
              {m.auth.ackLabel} <Link to="/privacy">{m.common.privacy}</Link> ·{" "}
              <Link to="/terms">{m.common.terms}</Link>
            </span>
          </label>
          {errors.ack ? (
            <span id="ack-error" className={ui.fieldError} role="alert">
              {errors.ack}
            </span>
          ) : null}
        </div>
        {formError ? <StatePanel kind="error" title={m.auth.registerFailed} message={formError} /> : null}
        <Button type="submit" disabled={busy} aria-busy={busy || undefined}>
          {busy ? m.common.creating : m.auth.registerAction}
        </Button>
        <p className={ui.meta}>
          {m.auth.hasAccount} <Link to="/login">{m.common.signIn}</Link>
        </p>
      </form>
    </AuthLayout>
  );
}

function AuthLayout({ title, lead, children }: { title: string; lead: string; children: ReactNode }) {
  const { m } = useLocale();
  return (
    <div className={`${styles.split} fade-in`}>
      <aside className={styles.brandPane} aria-hidden={false}>
        <div>
          <Wordmark to="/" />
          <h1>{adminHost ? m.admin.title : m.landing.headline.split("\n").join(" ")}</h1>
          {!adminHost && <p>{m.landing.lead}</p>}
        </div>
        {!adminHost && <p className={ui.meta}>{m.landing.pilotNote}</p>}
      </aside>
      <div className={styles.formPane}>
        <header className={styles.formTop}>
          <div className={styles.mobileMark}>
            <Wordmark to="/" />
          </div>
          <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", marginLeft: "auto" }}>
            <LanguageSwitch />
            {!adminHost && <Link to="/">{m.common.toSite}</Link>}
          </div>
        </header>
        <main id="main" className={styles.formMain}>
          <h1>{title}</h1>
          <p>{lead}</p>
          {children}
        </main>
        <footer className={styles.formFooter}>
          <Link to="/privacy">{m.common.privacy}</Link>
          <Link to="/terms">{m.common.terms}</Link>
        </footer>
      </div>
    </div>
  );
}
